import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Avatar,
    Card,
    CardContent,
    Button,
    Grid,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import CenterCard from "../../Components/CenterCard";
import AddUserModal from "../../Components/AddUserModal";

const ClubDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [clubData, setClubData] = useState(null);
    const [clubUsers, setClubUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

    const fetchClubDetails = async () => {
        setIsLoading(true);
        try {
            const response = await AxiosInstance?.get(`${ApiList?.GET_CLUB_DETAILS}?club_id=${id}`);
            if (response?.data?.s === 1) {
                setClubData(response?.data?.r);
            }
        } catch (error) {
            console.log("Error fetching club details:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchClubUsers = async () => {
        setIsLoadingUsers(true);
        try {
            const response = await AxiosInstance?.get(`${ApiList?.GET_CLUB_USERS}?club_id=${id}`);
            if (response?.data?.s === 1) {
                setClubUsers(response?.data?.d || []);
            }
        } catch (error) {
            console.log("Error fetching club users:", error);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchClubDetails();
            fetchClubUsers();
        }
    }, [id]);

    const handleBack = () => {
        navigate(-1);
    };

    const handleAddUser = () => {
        setIsAddUserModalOpen(true);
    };

    const handleCloseAddUserModal = () => {
        setIsAddUserModalOpen(false);
    };

    const handleUserAdded = () => {
        fetchClubUsers(); // Refresh users list
    };

    const renderField = (label, value) => (
        <Box sx={{ mb: 2 }}>
            <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#666", mb: 0.5 }}
            >
                {label}
            </Typography>
            <Typography variant="body1" sx={{ color: "#333" }}>
                {value || "-"}
            </Typography>
        </Box>
    );

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%", height: "100vh", overflow: "auto", p: 3 }}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 3,
                    gap: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Button
                        onClick={handleBack}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            color: "#000938",
                            textTransform: "none",
                            fontWeight: 600,
                        }}
                    >
                        Back
                    </Button>
                    <Typography
                        sx={{
                            fontSize: { xs: "24px", lg: "26px" },
                            fontWeight: "600",
                            color: "#000938",
                        }}
                    >
                        Club Details
                    </Typography>
                </Box>
                <Button
                    onClick={handleAddUser}
                    startIcon={<PersonAddIcon />}
                    sx={{
                        background: "#00D8A2",
                        color: "#000938",
                        borderRadius: "15px",
                        fontWeight: "600",
                        textTransform: "none",
                        px: 3,
                        "&:hover": {
                            background: "#00C294",
                        },
                    }}
                >
                    Add User
                </Button>
            </Box>

            {clubData && (
                <Card sx={{ maxWidth: 1200, mx: "auto", boxShadow: 3 }}>
                    <CardContent sx={{ p: 4 }}>
                        {/* Club Header */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 4,
                                gap: 3,
                            }}
                        >
                            {clubData.logo ? (
                                <CenterCard
                                    cover_img={clubData.logo}
                                    sx={{
                                        width: "80px",
                                        height: "80px",
                                        objectFit: "cover",
                                        boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                                        borderRadius: "12px",
                                    }}
                                    style={{ objectFit: "cover", borderRadius: "12px" }}
                                />
                            ) : (
                                <Avatar sx={{ width: 80, height: 80 }} />
                            )}
                            <Box>
                                <Typography
                                    variant="h4"
                                    sx={{ fontWeight: 600, color: "#000938", mb: 1 }}
                                >
                                    {clubData.club_name || "-"}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{ color: "#666", mb: 1 }}
                                >
                                    {clubData.club_code || "-"}
                                </Typography>
                                <Chip
                                    label={clubData.rds_status || "inactive"}
                                    color={clubData.rds_status === "active" ? "success" : "default"}
                                    sx={{ textTransform: "capitalize" }}
                                />
                            </Box>
                        </Box>

                        {/* Club Details Grid */}
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 2, height: "100%" }}>
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 600, mb: 2, color: "#000938" }}
                                    >
                                        Basic Information
                                    </Typography>
                                    {renderField("Club ID", clubData.id)}
                                    {/* {renderField("User ID", clubData.user_id)} */}
                                    {/* {renderField("Region", clubData.region)} */}
                                    {renderField("Description", clubData.description)}
                                    {renderField("Status", clubData.status === 1 ? "Active" : "Inactive")}
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 2, height: "100%" }}>
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 600, mb: 2, color: "#000938" }}
                                    >
                                        Location Details
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                                        <LocationOnIcon sx={{ color: "#666", mr: 1, mt: 0.5 }} />
                                        <Box>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 600, color: "#666", mb: 0.5 }}
                                            >
                                                Location
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: "#333" }}>
                                                {clubData.location || "-"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {renderField("Latitude", clubData.lat)}
                                    {renderField("Longitude", clubData.lng)}
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 2, height: "100%" }}>
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 600, mb: 2, color: "#000938" }}
                                    >
                                        Database Information
                                    </Typography>
                                    {/* {renderField("Schema Name", clubData.schema_name)} */}
                                    {renderField("RDS Status", clubData.rds_status)}
                                    {renderField("RDS Instance ID", clubData.rds_instance_id)}
                                    {renderField("RDS Endpoint", clubData.rds_endpoint)}
                                    {renderField("RDS Username", clubData.rds_username)}
                                    {renderField("RDS Region", clubData.rds_region)}
                                    {renderField("DB Instance Class", clubData.db_instance_class)}
                                    {/* {renderField("RDS Error", clubData.rds_error)} */}
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 2, height: "100%" }}>
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 600, mb: 2, color: "#000938" }}
                                    >
                                        Timestamps
                                    </Typography>
                                    {renderField(
                                        "Created At",
                                        clubData.created_at
                                            ? new Date(clubData.created_at).toLocaleString()
                                            : "-"
                                    )}
                                    {renderField(
                                        "Updated At",
                                        clubData.updated_at
                                            ? new Date(clubData.updated_at).toLocaleString()
                                            : "-"
                                    )}
                                </Card>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Club Users Section */}
            {clubData && (
                <Card sx={{ maxWidth: 1200, mx: "auto", boxShadow: 3, mt: 3 }}>
                    <CardContent sx={{ p: 4 }}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 3,
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, color: "#000938" }}
                            >
                                Club Users ({clubUsers.length})
                            </Typography>
                        </Box>

                        {isLoadingUsers ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    py: 4,
                                }}
                            >
                                <Typography>Loading users...</Typography>
                            </Box>
                        ) : clubUsers.length > 0 ? (
                            <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                            <TableCell sx={{ fontWeight: 600 }}>Profile</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {clubUsers.map((user, index) => (
                                            <TableRow key={user.id || index}>
                                                <TableCell>
                                                    {user.profile_img ? (
                                                        <CenterCard
                                                            cover_img={user.profile_img}
                                                            sx={{
                                                                width: "40px",
                                                                height: "40px",
                                                                objectFit: "cover",
                                                                borderRadius: "50%",
                                                            }}
                                                            style={{ objectFit: "cover", borderRadius: "50%" }}
                                                        />
                                                    ) : (
                                                        <Avatar sx={{ width: 40, height: 40 }}>
                                                            {user.full_name?.charAt(0)?.toUpperCase() || "U"}
                                                        </Avatar>
                                                    )}
                                                </TableCell>
                                                <TableCell>{user.full_name || "-"}</TableCell>
                                                <TableCell>{user.email || "-"}</TableCell>
                                                <TableCell>
                                                    {user.phno
                                                        ? `+${user.phno_cc || ""} ${user.phno}`
                                                        : "-"
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={
                                                            user.role === 1
                                                                ? "Club Manager"
                                                                : user.role === 2
                                                                    ? "Coordinator"
                                                                    : user.role === 3
                                                                        ? "Scout"
                                                                        : "Unknown"
                                                        }
                                                        color={
                                                            user.club_role === 1
                                                                ? "primary"
                                                                : user.club_role === 2
                                                                    ? "secondary"
                                                                    : user.club_role === 3
                                                                        ? "info"
                                                                        : "default"
                                                        }
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={user.status === 1 ? "Active" : "Inactive"}
                                                        color={user.status === 1 ? "success" : "default"}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {user.created_at
                                                        ? new Date(user.created_at).toLocaleDateString()
                                                        : "-"
                                                    }
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    py: 6,
                                    color: "#666",
                                }}
                            >
                                <PersonAddIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    No users found
                                </Typography>
                                <Typography variant="body2">
                                    Click "Add User" to add the first user to this club
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Add User Modal */}
            <AddUserModal
                open={isAddUserModalOpen}
                onClose={handleCloseAddUserModal}
                clubData={clubData}
                onSuccess={handleUserAdded}
            />
        </Box>
    );
};

export default ClubDetails;